import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { portalApi } from '../../../api/portal.js';
import { ApiError } from '../../../api/client.js';

/**
 * Portal profile and privacy. Warm-styled counterpart to the admin AccountPage:
 * shows the owner's details, lets them download their data (GDPR art. 15) and
 * erase their account and pet data (art. 17), confirmed with their password.
 */
export function PortalProfile() {
  const { customer } = useOutletContext();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [exportUrl, setExportUrl] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');

  const exportMut = useMutation({
    mutationFn: () => portalApi.requestDataExport(),
    onSuccess: (data) => setExportUrl(data.downloadUrl || null)
  });

  const eraseMut = useMutation({
    mutationFn: () => portalApi.erase({ password }),
    onSuccess: async () => { await logout(); navigate('/login', { replace: true }); }
  });

  const eraseError = eraseMut.isError
    ? (eraseMut.error instanceof ApiError && eraseMut.error.statusCode === 400
        ? 'Password non corretta.'
        : 'Operazione non riuscita. Riprova.')
    : null;

  const name = (customer && [customer.firstName, customer.lastName].filter(Boolean).join(' '))
    || (user && user.firstName) || '';

  return (
    <div className="space-y-6 pt-2">
      <h2 className="text-2xl font-semibold text-portal-ink">Il tuo profilo</h2>

      <div className="rounded-portal bg-portal-card p-6 shadow-portal">
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-portal-ink-muted">Nome</dt>
            <dd className="text-portal-ink">{name || '-'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-portal-ink-muted">Email</dt>
            <dd className="text-portal-ink">{(customer && customer.email) || (user && user.email) || '-'}</dd>
          </div>
          {customer && customer.phone && (
            <div className="flex justify-between gap-4">
              <dt className="text-portal-ink-muted">Telefono</dt>
              <dd className="text-portal-ink">{customer.phone}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Data export (art. 15) */}
      <div className="rounded-portal bg-portal-card p-6 shadow-portal">
        <h3 className="font-semibold text-portal-ink">I tuoi dati</h3>
        <p className="mt-1 text-sm text-portal-ink-muted">
          Scarica una copia di tutti i dati che conserviamo su di te e sul tuo animale.
        </p>
        <button
          type="button"
          onClick={() => exportMut.mutate()}
          disabled={exportMut.isPending}
          className="mt-3 rounded-full bg-portal-brand px-5 py-2 text-sm font-medium text-white hover:bg-portal-brand-hover disabled:opacity-60"
        >
          {exportMut.isPending ? 'Preparo…' : 'Scarica i miei dati'}
        </button>
        {exportUrl && (
          <p className="mt-3 text-sm">
            <a href={exportUrl} className="text-portal-brand underline">Scarica il file</a>
            <span className="text-portal-ink-muted"> (il link scade tra 24 ore).</span>
          </p>
        )}
      </div>

      {/* Erasure (art. 17) */}
      <div className="rounded-portal border border-portal-border bg-portal-card p-6 shadow-portal">
        <h3 className="font-semibold text-portal-ink">Elimina account e dati</h3>
        <p className="mt-1 text-sm text-portal-ink-muted">
          Questa azione è definitiva. Eliminiamo il tuo account e i dati del tuo animale (foto, diario,
          consulenze). Non sarà più possibile accedere.
        </p>

        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="mt-3 rounded-full border border-portal-brand px-5 py-2 text-sm font-medium text-portal-brand hover:bg-portal-brand-soft"
          >
            Elimina i miei dati
          </button>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); if (password) eraseMut.mutate(); }}
            className="mt-4 space-y-3"
          >
            <label className="block text-sm font-medium text-portal-ink">
              Conferma con la tua password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1 w-full rounded-portal border border-portal-border bg-portal-bg px-4 py-2.5 text-portal-ink outline-none focus:border-portal-brand"
              />
            </label>
            {eraseError && <p className="text-sm text-portal-brand">{eraseError}</p>}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={!password || eraseMut.isPending}
                className="rounded-full bg-portal-brand px-5 py-2 text-sm font-medium text-white hover:bg-portal-brand-hover disabled:opacity-60"
              >
                {eraseMut.isPending ? 'Elimino…' : 'Elimina definitivamente'}
              </button>
              <button
                type="button"
                onClick={() => { setConfirming(false); setPassword(''); }}
                className="rounded-full px-5 py-2 text-sm text-portal-ink-muted hover:text-portal-ink"
              >
                Annulla
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
